/**
 * Guardas de exibição da probabilidade (§14.6.4; FE §3.13, Fase 6). Pura: decide se o bloco `probability` do snapshot
 * aparece na vista e com que `status`/`band`; `selectView` a usa como `probabilityPolicy` padrão (injetável). Nada de
 * cálculo aqui (PE-13): o motor roda no publicador; o cliente só aplica as 7 regras de §14.6.4, na ordem, mais a
 * segunda trava de E01 pela hora do SERVIDOR (§16.5, AC-16.5-01).
 *
 * `reasonKey` de §14.6.4 fica de fora: `model/` não importa `copy/` (FE §4.2) e o motivo exibido é o `public_reason`
 * do payload (catálogo fechado P-04, validado em I-14), lido pelo `ProbabilitySlot`.
 */
import type { ChanceBand, ProbabilityPayload, ProbabilityStatus } from '../contracts/probability';
import type { Snapshot } from '../contracts/snapshot';
import { epochMs, T_OPEN, tMinOfIso } from './time';

/** Resultado das guardas (§14.6.4, sem a chave de texto). */
export interface ProbabilityDisplay {
  visible: boolean;
  status: ProbabilityStatus;
  band: ChanceBand | null;
}

/** Guarda de idade (§14.6.4 regra 5): `published_at − generated_at > 180 s` (valor inicial, a calibrar, PE-13). */
export const PROBABILITY_STALE_MS = 180_000;
/** Guarda de idade (§14.6.4 regra 5): `base_seq < seq − 3`. */
export const PROBABILITY_STALE_SEQS = 3;

/** E01 (§16.5): `published_at` antes das 17h00 de Brasília do dia da eleição (hora do servidor, nunca do aparelho). */
export function snapshotBeforeOpen(snap: Pick<Snapshot, 'clocks' | 'election'>): boolean {
  const t = tMinOfIso(snap.clocks.published_at, epochMs(snap.election.date));
  return !(t >= T_OPEN);
}

/**
 * A chamada "acima de 99%" aprovada (P-30 item 1): `call.state = 'above_99'` com `approved_at`. Só ela libera ">99%",
 * "<1%", a frase "mais de 99" e a faixa `above_99`; o número sozinho nunca (fmtChancePairShown).
 */
export function chanceCalled(p: Pick<ProbabilityPayload, 'call'>): boolean {
  return p.call?.state === 'above_99' && !!p.call.approved_at;
}

/**
 * §14.6.4, na ordem: (1) flag desligada → `off`; (2) relógio antes das 17h00 — e, pela trava dupla de E01, snapshot
 * publicado antes das 17h00 — → invisível; (3) sem payload → `error`; (4) presidência definida → `ended`; (5) payload
 * velho → `paused`; (6) `above_99` sem chamada aprovada → `very_likely`; (7) visível só com `active`, `waiting_data` ou
 * `paused`.
 */
export function displayProbability(
  p: ProbabilityPayload | null,
  snap: Snapshot,
  clockTMin: number,
  featureOn: boolean,
): ProbabilityDisplay {
  if (!featureOn) return { visible: false, status: 'off', band: null };
  if (clockTMin < T_OPEN || snapshotBeforeOpen(snap)) {
    return { visible: false, status: p?.status ?? 'off', band: null };
  }
  if (p === null) return { visible: false, status: 'error', band: null };
  if (snap.races.president.national.math_decision !== null) return { visible: false, status: 'ended', band: null };
  const stale =
    Date.parse(snap.clocks.published_at) - Date.parse(p.generated_at) > PROBABILITY_STALE_MS ||
    p.base_seq < snap.seq - PROBABILITY_STALE_SEQS;
  let band = p.band;
  if (band === 'above_99' && !chanceCalled(p)) band = 'very_likely';
  const status: ProbabilityStatus = stale && p.status === 'active' ? 'paused' : p.status;
  const visible = status === 'active' || status === 'waiting_data' || status === 'paused';
  return { visible, status, band };
}
